import { Designer } from 'interfaces/designer'
import { ActivityNotification } from 'interfaces/notifications'
import { AdminProjectTemplate, AdminProjectTemplateParams, ProjectTemplate } from 'interfaces/project-template'
import { SavedSearch } from 'interfaces/saved-search'
import { SearchV2Response, SearchResponse, Ticket, SearchTicket, SearchV2Ticket } from 'interfaces/ticket'
import qs from 'query-string'
import { Announcement } from './announcements/announcements'
import { TicketData } from './tickets/tickets'
import { Project } from 'interfaces/project'
import { FileExport } from 'interfaces/file-exports'
import { SkillCategory } from './skills/skills'
import { AssetableType, CustomerState, TicketFile } from './ticket-files/ticket-files'
import { Skill } from 'interfaces/skill'

interface Endpoint {
  path: string
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'BLOB'
  baseUrl?: string
}

export const endpoints: Record<string, Endpoint> = {
  addTicketsToProject: { path: '/projects/:id/add_tickets', method: 'PATCH' },
  exportAssets: { path: '/external_asset', method: 'POST' },
  createFileExport: { path: '/file_exports', method: 'POST' },
  createProject: { path: '/projects', method: 'POST' },
  createProjectTemplate: { path: '/admin/project_templates', method: 'POST' },
  createSavedSearch: { path: '/saved_searches', method: 'POST' },
  deleteSavedSearch: { path: '/saved_searches/:id', method: 'DELETE' },
  duplicateProject: { path: '/projects/:id/duplicate', method: 'PATCH' },
  getAllAdminProjectTemplates: { path: '/admin/project_templates', method: 'GET' },
  getAllCustomProjectTemplates: { path: '/project_templates', method: 'GET' },
  updateProjectTemplate: { path: '/admin/project_templates/:id', method: 'PATCH' },
  getMostUsedProjectTemplates: { path: '/project_templates/most_used', method: 'GET' },
  getAllTickets: { path: '/tickets/all_tickets', method: 'GET' },
  getAnnouncements: { path: '/announcements', method: 'GET' },
  getCompanyDesigners: { path: '/company_designers', method: 'GET' },
  getFileExports: { path: '/file_exports', method: 'GET' },
  getFileExportsFormats: { path: '/file_exports/formats', method: 'GET' },
  getNotifications: { path: '/notifications', method: 'GET' },
  getProject: { path: '/projects/:id', method: 'GET' },
  getProjectDeliverables: { path: '/projects/:id/tickets', method: 'GET' },
  getProjects: { path: '/projects', method: 'GET' },
  getProjectTemplate: { path: '/project_templates/:id', method: 'GET' },
  getSavedSearches: { path: '/saved_searches', method: 'GET' },
  getExternalSearch: { path: '/external_search', method: 'GET' },
  getSearch: { path: '/search', method: 'GET' },
  getTickets: { path: '/tickets', method: 'GET' },
  getUnreadCount: { path: '/notifications/unread_count', method: 'GET' },
  markNotificationRead: { path: '/notifications/mark_all_read', method: 'PATCH' },
  removeTicketsFromProject: { path: '/projects/:id/remove_tickets', method: 'PATCH' },
  updateFileExport: { path: '/file_exports/:id', method: 'PATCH' },
  updateProject: { path: '/projects/:id', method: 'PATCH' },
  updateTicketFile: { path: '/ticket_files/:ticket_file_id', method: 'PATCH' },
  updateTicketTicketFile: { path: '/tickets/:ticket_id/ticket_files/:ticket_file_id', method: 'PATCH' },
  deleteProject: { path: '/projects/:id', method: 'DELETE' },
  deleteProjectTemplate: { path: '/admin/project_templates/:id', method: 'DELETE' },
  getSkills: { path: '/skills', method: 'GET' },
  getAdminSkills: { path: '/admin/skills', method: 'GET' },
  bulkCreateTickets: { path: '/tickets/bulk_create', method: 'POST' },
}

interface Meta {
  nextPage: string | null
  pageSize: number
  previousPage: string | null
  total: number
  counters?: {
    inProgressTickets: number
    doneTickets: number
  }
}

interface Responses {
  createProject: {
    data: Project
  }
  duplicateProject: {
    data: Project
  }
  updateProject: {
    data: Project
  }
  updateProjectTemplate: {
    data: AdminProjectTemplate
  }
  createProjectTemplate: {
    data: AdminProjectTemplate
  }
}

export interface Endpoints {
  addTicketsToProject: { ticket_ids: number[] }
  exportAssets: { ticket_file_ids: number[] }
  createFileExport: { message: string }
  createProject: {
    name: string
    description: string
    project_template_id?: number
    tickets: {
      skill_ids: number[]
    }
  }
  createProjectTemplate: {
    project_template: AdminProjectTemplateParams
  }
  createSavedSearch: Pick<SavedSearch, 'name' | 'parameters'>
  deleteSavedSearch: { id: string }
  duplicateProject: { id: number }
  getAllAdminProjectTemplates: { data: AdminProjectTemplate[]; meta: Meta }
  getAllCustomProjectTemplates: { data: ProjectTemplate[]; meta: Meta }
  getMostUsedProjectTemplates: { data: ProjectTemplate[] }
  updateProjectTemplate: {
    project_template: AdminProjectTemplateParams
  }
  getAllTickets: { data: Ticket[]; meta: Meta }
  getAnnouncements: Announcement[]
  getCompanyDesigners: { data: Designer[] }
  getFileExports: { data: FileExport[]; meta: Meta }
  getFileExportsFormats: { data: string[] }
  getNotifications: { data: ActivityNotification[]; meta: Meta }
  getProject: { data: Project }
  getProjectDeliverables: { data: Ticket[]; meta: Meta }
  getProjects: { data: Project[] }
  getProjectTemplate: ProjectTemplate
  getSavedSearches: { data: SavedSearch[] }
  getSearch: SearchResponse<SearchTicket | Project>
  getExternalSearch: SearchV2Response<SearchV2Ticket | Project>
  getTickets: TicketData
  getUnreadCount: { data: { unread_count: number } }
  markNotificationRead: null
  removeTicketsFromProject: { ticket_ids: number[] }
  updateFileExport: Pick<FileExport, 'display'>
  updateProject: {
    name?: string
    brand_id?: number
    description?: string
  }
  updateTicketFile: { assetable_type: AssetableType; assetable_id: number }[]
  updateTicketTicketFile: TicketFile
  deleteProject: null
  deleteProjectTemplate: null
  getSkills: SkillCategory[]
  getAdminSkills: { data: Skill[]; meta: Meta }
  bulkCreateTickets: {
    skill_ids: number[]
    project_id: number | null
  }
}

interface Queries {
  addTicketsToProject: { id: number }
  exportAssets: null
  createFileExport: {
    ticket_file_id: number
    output_format: string
  }
  createProject: null
  createProjectTemplate: null
  createSavedSearch: null
  deleteSavedSearch: { id: string }
  duplicateProject: { id: number }
  getAllCustomProjectTemplates: { page: number; page_size?: number; partner?: boolean }
  getAllTickets: { page: number; page_size?: number; subject?: string }
  updateProjectTemplate: { id: number }
  getMostUsedProjectTemplates: null
  getAnnouncements: null
  getCompanyDesigners: null
  getFileExports: { ticket_id: number }
  getFileExportsFormats: { ticket_file_id: number }
  getNotifications: { page: number; page_size?: number }
  getProject: { id: number }
  getProjectDeliverables: { id: number; page: number }
  getAllAdminProjectTemplates: { page: number }
  getProjects: null
  getProjectTemplate: { id: number }
  getSavedSearches: null
  getSearch: {
    page?: string
    page_size?: number
    query?: string
    sort_key?: string
    sort_order?: string
  }
  getExternalSearch: {
    page?: string
    page_size?: number
    query?: string
    sort_key?: string
    sort_order?: string
    'index[]': string[]
  }
  getTickets: {
    first_render?: string
    page: number
    sort_column?: string
    user_id?: number
  }
  getUnreadCount: null
  markNotificationRead: null
  removeTicketsFromProject: { id: number }
  updateFileExport: { id: number }
  updateProject: { id: number }
  updateTicketFile: { ticket_file_id: number }
  updateTicketTicketFile: { ticket_id: number; ticket_file_id: number; customer_state: CustomerState }
  deleteProject: { id: number }
  deleteProjectTemplate: { id: number }
  getSkills: null
  getAdminSkills: { page: number; q: { name_cont: string } }
  bulkCreateTickets: null
}

interface RequestOptions<T extends keyof Endpoints> extends Omit<RequestInit, 'body'> {
  body?: Endpoints[T]
}

interface RequestObject<T extends keyof Endpoints & keyof Queries> {
  endpoint: keyof typeof endpoints & T
  query?: Queries[T]
  requestOptions?: Omit<RequestOptions<T>, 'body'>
  body?: Endpoints[T]
}

export const request = async <T extends keyof Endpoints>({
  endpoint,
  query,
  requestOptions,
  body,
}: RequestObject<T>): Promise<T extends keyof Responses ? Responses[T] : Endpoints[T]> => {
  const { path, method, baseUrl } = endpoints[endpoint]

  const url = getUrl(`${baseUrl || ''}/api/internal${path}`, query)

  const req = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...requestOptions?.headers,
    },
    ...requestOptions,
    body: body ? JSON.stringify(body) : undefined,
  })

  // Todo: standardize errors
  if (!req.ok) {
    throw new Error(`Request failed: ${req.statusText}`)
  }

  return req.json()
}

// Makes use-query snytax better
export const requestQuery =
  <T extends keyof Endpoints>(object: RequestObject<T>) =>
  () => {
    return request(object)
  }

export const getUrl = <T extends keyof Queries>(url: string, query: Queries[T]) => {
  if (!query) {
    return url
  }

  const newQuery = {}

  // Replace placeholders in the URL with the query parameters and encode them, any other query parameters are added as query string at the end of the URL
  const encodedPath = Object.entries(query).reduce((acc, [key, value]) => {
    if (acc.includes(`:${key}`) && key !== 'q') {
      return acc.replace(`:${key}`, encodeURIComponent(value as string))
    }

    if (key !== 'q') {
      newQuery[key] = value
    }
    return acc
  }, url)

  let queryString = decodeURIComponent(qs.stringify(newQuery))
  if (query['q']) {
    queryString = `${queryString}${ransackQuery(query['q'])}`
  }
  const fullUrl = queryString ? `${encodedPath}?${queryString}` : encodedPath

  return fullUrl
}

const ransackQuery = (qValue: string) => {
  return `&${Object.entries(qValue)
    .map(([key, value]) => `q[${key}]=${value}`)
    .join('&')}`
}
